import { useState, useEffect, useRef } from "react";
import { wabaApi } from "../../lib/wabaApi";

// Configuração "Geral v4": Embedded Signup do WhatsApp + ativos de Páginas e
// Contas do Instagram — um login só coleta os três canais. O backend
// (/link-complete) troca o code uma vez e liga o que o cliente concedeu.
const META_APP_ID = "843063592187222";
const COMPLETE_CONFIG_ID = "1507947634707534";

interface Props {
  connectionId: string;
  onSuccess: () => void;
}

type SignupSession = {
  phone_number_id?: string;
  waba_id?: string;
  business_id?: string;
  page_ids?: string[];
  instagram_account_ids?: string[];
};

type LinkResults = { whatsapp?: unknown; instagram?: unknown; messenger?: unknown };

const CANAL_LABEL = { whatsapp: "WhatsApp", instagram: "Instagram", messenger: "Messenger" } as const;

/** Texto legível do resultado — mesmo racional do errorText dos outros signups (React #31). */
function resultText(v: unknown): string {
  if (typeof v === "string") return v;
  if (v && typeof v === "object") {
    const d = v as any;
    return d.meta_message || d.error || JSON.stringify(d);
  }
  return String(v);
}

export function CompleteSignup({ connectionId, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<LinkResults | null>(null);
  const sessionRef = useRef<SignupSession>({});

  // Carrega o SDK da Meta (idempotente — mesmo script dos outros signups).
  useEffect(() => {
    if (document.getElementById("fb-jssdk")) return;
    window.fbAsyncInit = () => {
      window.FB.init({ appId: META_APP_ID, autoLogAppEvents: true, xfbml: true, version: "v25.0" });
    };
    const script = document.createElement("script");
    script.id = "fb-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true; script.defer = true;
    document.body.appendChild(script);
  }, []);

  // postMessage do Embedded Signup — no v4 o FINISH traz também page_ids e
  // instagram_account_ids, que dizem ao backend quais canais tentar.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const allowedOrigins = ["https://www.facebook.com", "https://web.facebook.com"];
      if (!allowedOrigins.includes(event.origin)) return;
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type !== "WA_EMBEDDED_SIGNUP") return;
        console.log("WA_EMBEDDED_SIGNUP event (completo):", data);

        const isFinish = data?.event === "FINISH" || data?.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
        if (isFinish) {
          sessionRef.current = {
            phone_number_id: data.data?.phone_number_id,
            waba_id: data.data?.waba_id,
            business_id: data.data?.business_id ?? data.data?.businessId,
            page_ids: data.data?.page_ids,
            instagram_account_ids: data.data?.instagram_account_ids,
          };
        }
        if (data?.event === "ERROR") {
          setError(data.data?.error_message || data.data?.message || "Erro no cadastro da Meta.");
          setLoading(false);
        }
        if (data?.event === "CANCEL") {
          setError("Cadastro cancelado.");
          setLoading(false);
        }
      } catch {}
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  async function doLink(code: string) {
    const s = sessionRef.current;
    try {
      const out = await wabaApi.post<{ connection: unknown; results: LinkResults }>(
        `/connections/${connectionId}/link-complete`,
        {
          code,
          phone_number_id: s.phone_number_id,
          waba_id: s.waba_id,
          business_id: s.business_id,
          page_ids: s.page_ids ?? [],
          instagram_account_ids: s.instagram_account_ids ?? [],
        },
      );
      setResults(out.results);
      // Algum canal ligou → recarrega o card; os que falharam mantêm o botão
      // individual da própria seção como retry.
      if (Object.values(out.results).some((v) => v === "ok")) onSuccess();
    } catch (err: any) {
      console.error("Erro no vínculo completo:", err);
      const d = err?.payload?.detail;
      setError(typeof d === "string" ? d : "Erro ao vincular os canais.");
    } finally { setLoading(false); }
  }

  function handleSignup() {
    if (!window.FB) { setError("SDK da Meta ainda não carregou. Aguarde e tente novamente."); return; }
    setError(null); setResults(null); setLoading(true);
    sessionRef.current = {};

    // Callback SÍNCRONO — o SDK recusa async function (mesmo padrão dos outros).
    window.FB.login((response: any) => {
      const code = response?.authResponse?.code;
      if (!code) {
        setError("Cadastro cancelado ou código não retornado pela Meta.");
        setLoading(false); return;
      }
      doLink(code);
    }, {
      config_id: COMPLETE_CONFIG_ID,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        setup: {},
        // Mesmo feature_type do signup individual: mantém o caminho coex
        // (número já no app WhatsApp Business) também no fluxo completo.
        featureType: "whatsapp_business_app_onboarding",
      },
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        onClick={handleSignup}
        disabled={loading}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "9px 18px",
          background: loading ? "var(--surface-input)" : "#1877F2",
          color: loading ? "var(--ink-muted)" : "#fff", border: "none", borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: loading ? "default" : "pointer",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
        {loading ? "Conectando..." : "Conectar tudo (WhatsApp + Instagram + Messenger)"}
      </button>
      {error && <span style={{ fontSize: 12, color: "var(--danger)" }}>{error}</span>}
      {results && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {(Object.keys(CANAL_LABEL) as Array<keyof typeof CANAL_LABEL>).map((c) => {
            const v = results[c];
            // Canal que o cliente não incluiu no login não vira ruído.
            if (v == null || v === "skipped") return null;
            const ok = v === "ok";
            return (
              <span key={c} style={{ fontSize: 12, color: ok ? "var(--ink-secondary)" : "var(--danger)" }}>
                {CANAL_LABEL[c]}: {ok ? "conectado" : resultText(v)}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
